import type {
  Lab,
  Paginated,
  Trade,
  TradeRequirement,
  TradesInstanceSource,
  TradesInstanceStatus,
} from 'types/models';
import type { LabTrade, LabTradeMinimal } from 'types/models/lab_trade';
import type { MeaValidationBrief } from 'types/models/mea_validation_brief';

export type GetAdminLabTradesParams = {
  page: number;
  lab_id?: number;
  query?: string;
};

export type AdminLabTradeItem = Pick<
  LabTrade,
  'id' | 'created_at' | 'is_faks_validation_enabled' | 'mea_brief'
> & {
  lab: Pick<Lab, 'id' | 'name'>;
  trades: Pick<Trade, 'id'>[];
  not_refusing_participation_count: number;
  main_trade: Pick<Trade, 'title'> & {
    sellout_requirement: Pick<TradeRequirement, 'id'> | null;
    picture_requirement: Pick<TradeRequirement, 'id'> | null;
  };
};

export type GetAdminLabTradesResponse = Paginated<{ lab_trades: AdminLabTradeItem[] }>;

export type GetValidatedParticipationsCountResponse = {
  validated_participations_count: number;
};

export type RecalculateLabTradeCompensationsParams = {
  recalculate_validated: boolean;
  reset_override: boolean;
  reason: string;
};

export type AdminExternalParticipationCheckParams = {
  pharmacy_id: number;
  master_trade_id: number;
};

type Requirement = Pick<
  TradeRequirement,
  'id' | 'kind' | 'name' | 'description' | 'start_date' | 'end_date'
>;

export type AdminExternalParticipationCheckResponse = {
  warning:
    | 'faks_participation_exists'
    | 'faks_participation_with_requirements_instance_exists'
    | 'external_participation_exists'
    | 'pharmacy_not_targeted'
    | 'trade_not_accessible'
    | 'external_participation_with_requirements_instance_exists';
  trade: Pick<Trade, 'id' | 'kind' | 'status' | 'participation_window_extended_until'> & {
    picture_requirement: Requirement | null;
    sellout_requirement: Requirement | null;
    mandate_contract_file_url: string | null;
  };
};

export type CreateExternalParticipationStatus = Extract<
  TradesInstanceStatus,
  'awaiting_validation_by_lab' | 'validated_by_lab' | 'refused_by_lab' | 'compensated_outside_faks'
>;

export type AdminCreateExternalParticipationParams = {
  master_trade_id: number;
  pharmacy_id: number;
  group_id: number;
  source: TradesInstanceSource;
  status: CreateExternalParticipationStatus;
  lab_refusal_reason?: string;
  picture_proof?: {
    attachment_signed_ids: string[];
  };
  sellout_proof?: {
    attachment_signed_ids: string[];
    sellout_total_item_quantities: number;
    sellout_total_item_sales_in_euros_cents: number;
  };
};

export type GetAdminLabTradeValidationBriefResponse = {
  validation_brief: MeaValidationBrief;
};

export type AdminCreateOrUpdateValidationBriefParams = {
  ai_prevalidation_enabled: boolean;
  ai_prompt?: string;
  human_brief?: string;
  ai_test_mode: boolean;
  auto_publish: boolean;
  needs_additional_validation: boolean;
  additional_documents_signed_ids?: string[];
};

export type GetLabTradesMinimalParams = {
  query?: string;
  ids?: number[];
  lab_id?: number;
};

export type GetLabTradesMinimalResponse = { master_trades: LabTradeMinimal[] };

export const manualValidationStatuses = ['to_be_treated', 'to_publish', 'all'] as const;
export type ManualValidationStatus = (typeof manualValidationStatuses)[number];

export const manualValidationTradeTypes = ['mea', 'listing', 'mea_and_listing'] as const;
export type ManualValidationTradeType = (typeof manualValidationTradeTypes)[number];

export type GetManualValidationMasterTradesParams = {
  status?: ManualValidationStatus;
  page?: number;
};

export type ManualValidationMasterTrade = Pick<LabTrade, 'id'> & {
  main_trade: Pick<Trade, 'title'>;
  lab: Pick<Lab, 'id' | 'name' | 'logo_url'>;
  awaiting_count: number;
  overdue_count: number;
  trade_type: ManualValidationTradeType;
};

export type GetManualValidationMasterTradesResponse = Paginated<{
  master_trades: ManualValidationMasterTrade[];
}>;

export type GetManualValidationParticipationIdsParams = {
  status: ManualValidationStatus;
};

export type GetManualValidationParticipationIdsResponse = {
  participation_ids: number[];
};

export type GetManualValidationMasterTradeResponse = Pick<LabTrade, 'id'> & {
  main_trade: Pick<Trade, 'title'>;
  lab: Pick<Lab, 'name' | 'logo_url'>;
};
